// apps/web/src/widgets/size-and-yield/renderer.tsx
//
// Phase 7 — WIDG-06 SizeAndYieldRenderer (D-01..D-04 typed config).
// Single-select Pill grid for discrete step selection + computed-yield display.
// State shape `{ selectedStepId: string }` per D-03. Per UI-SPEC
// §"size-and-yield renderer" (lines 247-258):
//   - empty steps[]        → "No sizes to pick from." fallback
//   - pre-selection / stale → "Pick a size" mini-title + "Yield: —" fallback
//   - per-step click       → onStateChange + aria-live='polite' yield announcement
// Yield-string formatting delegated to format-yield.ts (D-02 substitution).
import { Card, CardContent } from "@/components/ui/card";
import { Pill } from "@/components/ui/pill";
import type { WidgetRendererProps } from "../widget-renderer-props.js";
import { formatYield } from "./format-yield.js";

export function SizeAndYieldRenderer({
  widget,
  config,
  state,
  onStateChange,
}: WidgetRendererProps<"size-and-yield">): React.ReactElement {
  // State narrowing per D-03: StateForTemplate<"size-and-yield"> = { selectedStepId: string } | undefined.
  // A stale persisted state pointing at a step id that no longer exists in
  // config.steps[] degrades to the pre-selection UI (selectedStep = undefined).
  const selectedStepId: string | undefined =
    state !== undefined && "selectedStepId" in state ? state.selectedStepId : undefined;
  const selectedStep = config.steps.find((s) => s.id === selectedStepId);

  const yieldString =
    selectedStep !== undefined
      ? formatYield(config.yieldDisplay, selectedStep.multiplier)
      : undefined;

  const labelId = `${widget.instanceId}-label`;

  // Single-select toggle: clicking the same step is a no-op (cannot unselect a
  // size in v1; user must pick a different size to swap).
  const onPick = (stepId: string): void => {
    if (stepId === selectedStepId) return;
    onStateChange({ selectedStepId: stepId });
  };

  return (
    <Card className="mb-8">
      <CardContent className="p-4">
        {/* biome-ignore lint/a11y/useSemanticElements: section role="group" matches Phase 6 option-set convention; a native <fieldset> would inject legend styling and constrain inner layout. */}
        <section role="group" aria-labelledby={labelId} className="flex flex-col gap-2">
          <h3
            id={labelId}
            className="text-base font-semibold leading-tight text-[var(--recipe-ink)]"
          >
            {widget.name}
          </h3>

          {config.steps.length === 0 ? (
            <p className="text-sm text-[var(--recipe-muted)]">No sizes to pick from.</p>
          ) : (
            <>
              {selectedStep === undefined ? (
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--recipe-muted)]">
                  Pick a size
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {config.steps.map((step) => (
                  <Pill
                    key={step.id}
                    selected={step.id === selectedStepId}
                    disabled={false}
                    onClick={() => {
                      onPick(step.id);
                    }}
                  >
                    {step.label}
                  </Pill>
                ))}
              </div>
              {yieldString !== undefined ? (
                <p
                  aria-live="polite"
                  className="mt-3 text-sm font-semibold text-[var(--recipe-ink)]"
                >
                  {yieldString}
                </p>
              ) : (
                <p aria-live="polite" className="mt-3 text-sm text-[var(--recipe-muted)]">
                  Yield: —
                </p>
              )}
            </>
          )}
        </section>
      </CardContent>
    </Card>
  );
}
